import { useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  Badge, Button, Card,
} from '@openedx/paragon';
import { RiArrowDropRightLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from './messages';
import './QuizFlashcard.scss';

// How long the verdict + explanation stay up after the answer, before the card
// animates away. Long enough to read a one-line explanation.
const RESULT_HOLD_MS = 3000;

const QuizFlashcard = ({
  cardType, badgeText, text, courseLabel, footerActionLabel,
  back, options, correctOptionId, correctAnswer,
  onStart, onAttempt, isDismissing,
}) => {
  const intl = useIntl();
  const [pickedId, setPickedId] = useState(null);
  const [started, setStarted] = useState(false);

  const { quizOptions, correctId } = useMemo(() => {
    if (cardType === 'true_false') {
      return {
        quizOptions: [
          { id: 'true', label: intl.formatMessage(messages.flashcardTrue) },
          { id: 'false', label: intl.formatMessage(messages.flashcardFalse) },
        ],
        correctId: correctAnswer ? 'true' : 'false',
      };
    }
    return { quizOptions: options || [], correctId: correctOptionId };
  }, [cardType, options, correctOptionId, correctAnswer, intl]);

  const answered = pickedId !== null;
  const isCorrect = answered && pickedId === correctId;

  const start = () => {
    setStarted(true);
    onStart?.();
  };

  // Picking an answer IS the recall event, so it records the review straight
  // away — correct → Good (3), wrong → Again (1). Correctness is read off the
  // clicked option, not the `isCorrect` above, which is still a render behind.
  const pick = (optionId) => {
    if (answered) { return; }
    setPickedId(optionId);
    onAttempt?.(optionId === correctId ? 3 : 1, RESULT_HOLD_MS);
  };

  const renderInitial = () => (
    <>
      {badgeText && (
        <Badge className="flashcard-item__badge" variant="soft" color="primary" size="md">
          {badgeText}
        </Badge>
      )}
      <div className="flashcard-item__title">{text}</div>
      {courseLabel && (
        <div className="flashcard-item__courseLabel flashcard-item__courseLabel--quiz">{courseLabel}</div>
      )}
      <div className="quiz-flashcard__startSlot">
        <Button
          className="flashcard-item__cta"
          variant="ghost-primary"
          size="sm"
          iconAfter={RiArrowDropRightLine}
          onClick={start}
        >
          {footerActionLabel || intl.formatMessage(messages.flashcardStartQuiz)}
        </Button>
      </div>
    </>
  );

  const renderOptions = () => (
    <>
      {badgeText && (
        <Badge className="flashcard-item__badge" variant="soft" color="primary" size="md">
          {badgeText}
        </Badge>
      )}
      <div className="quiz-flashcard__question">{text}</div>
      <div className="quiz-flashcard__options">
        {quizOptions.map((option) => (
          <Button
            key={option.id}
            className="quiz-flashcard__option"
            variant="ghost-primary"
            size="sm"
            block
            onClick={() => pick(option.id)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </>
  );

  const renderResult = () => (
    <>
      {badgeText && (
        <Badge className="flashcard-item__badge" variant="soft" color="primary" size="md">
          {badgeText}
        </Badge>
      )}
      <div className="quiz-flashcard__result">
        <span
          className={classNames(
            'quiz-flashcard__verdict',
            isCorrect ? 'quiz-flashcard__verdict--correct' : 'quiz-flashcard__verdict--wrong',
          )}
        >
          {intl.formatMessage(
            isCorrect ? messages.flashcardAnswerCorrect : messages.flashcardAnswerIncorrect,
          )}
        </span>
        {back?.body && <p className="quiz-flashcard__explanation">{back.body}</p>}
      </div>
    </>
  );

  let body;
  if (answered) { body = renderResult(); } else if (started) { body = renderOptions(); } else { body = renderInitial(); }

  return (
    <Card
      className={classNames(
        'flashcard-item',
        isDismissing && 'flashcard-item--dismissing',
      )}
      variant="light"
    >
      <div className="flashcard-item__frame flashcard-item__frame--quiz">
        {body}
      </div>
    </Card>
  );
};

QuizFlashcard.propTypes = {
  cardType: PropTypes.oneOf(['mcq', 'true_false']).isRequired,
  badgeText: PropTypes.string,
  text: PropTypes.string.isRequired,
  courseLabel: PropTypes.string,
  footerActionLabel: PropTypes.string,
  onStart: PropTypes.func,
  /** (rating, holdMs) — called once, the moment an option is picked. */
  onAttempt: PropTypes.func,
  isDismissing: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })),
  correctOptionId: PropTypes.string,
  correctAnswer: PropTypes.bool,
  back: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    footnote: PropTypes.string,
  }),
};

export default QuizFlashcard;
